import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Ticket, Search, X, Copy, ToggleLeft, ToggleRight, Gift, Coins, Percent, Gamepad2, ChevronDown, Wallet } from 'lucide-react';
import ScopeSelector from '../components/ScopeSelector';
import { PageTooltip } from '../components/TooltipSystem';

const COUPON_TYPES = [
  { id: 'bonus_points', label: 'Punti Bonus', icon: Coins, color: '#3ECF8E', desc: 'Assegna punti bonus al cliente' },
  { id: 'discount', label: 'Sconto', icon: Percent, color: '#2196F3', desc: 'Sconto percentuale su acquisto' },
  { id: 'voucher', label: 'Buono €', icon: Wallet, color: '#0EA5E9', desc: 'Buono monetario riscattabile in più volte' },
  { id: 'prize', label: 'Premio', icon: Gift, color: '#9C27B0', desc: 'Regalo o prodotto gratuito' },
  { id: 'game_token', label: 'Gettone Gioco', icon: Gamepad2, color: '#FF9800', desc: 'Gettoni per giochi e partite extra' },
];

const TIERS = ['bronze', 'silver', 'gold', 'platinum'];

function TypeBadge({ type }) {
  const t = COUPON_TYPES.find(ct => ct.id === type);
  if (!t) return null;
  const Icon = t.icon;
  return (
    <span data-testid={`coupon-type-badge-${type}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold" style={{ background: t.color + '18', color: t.color }}>
      <Icon size={13} /> {t.label}
    </span>
  );
}

function CouponForm({ coupon, onSave, onClose }) {
  const isEdit = !!coupon;
  const [form, setForm] = useState({
    code: coupon?.code || '',
    coupon_type: coupon?.coupon_type || 'bonus_points',
    description: coupon?.description || '',
    discount_percent: coupon?.discount_percent || '',
    bonus_points: coupon?.bonus_points || '',
    prize_name: coupon?.prize_name || '',
    voucher_amount: coupon?.voucher_amount || '',
    voucher_min_per_use: coupon?.voucher_min_per_use || '',
    tokens_amount: coupon?.tokens_amount || '',
    max_uses_total: coupon?.max_uses_total || '',
    max_uses_per_customer: coupon?.max_uses_per_customer ?? 1,
    min_tier: coupon?.min_tier || 'bronze',
    min_age: coupon?.min_age || 0,
    valid_from: coupon?.valid_from ? coupon.valid_from.split('T')[0] : '',
    valid_until: coupon?.valid_until ? coupon.valid_until.split('T')[0] : '',
    is_active: coupon?.is_active ?? true,
    is_speed_challenge: coupon?.is_speed_challenge ?? false,
    speed_challenge_winners: coupon?.speed_challenge_winners ?? 1,
  });
  const [scope, setScope] = useState(coupon?.scope || 'brand');
  const [rc, setRc] = useState({
    min_wait_days: coupon?.redemption_conditions?.min_wait_days || '',
    allowed_weekdays: coupon?.redemption_conditions?.allowed_weekdays || [],
    time_from: coupon?.redemption_conditions?.time_from || '',
    time_until: coupon?.redemption_conditions?.time_until || '',
    min_visits: coupon?.redemption_conditions?.min_visits || '',
    min_spend: coupon?.redemption_conditions?.min_spend || '',
    cooldown_period: coupon?.redemption_conditions?.cooldown_period || '',
    cooldown_uses: coupon?.redemption_conditions?.cooldown_uses || 1,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // Clean numeric fields
      payload.discount_percent = payload.discount_percent ? parseFloat(payload.discount_percent) : null;
      payload.bonus_points = payload.bonus_points ? parseInt(payload.bonus_points) : null;
      payload.voucher_amount = payload.voucher_amount ? parseFloat(payload.voucher_amount) : null;
      payload.voucher_min_per_use = payload.voucher_min_per_use ? parseFloat(payload.voucher_min_per_use) : null;
      payload.tokens_amount = payload.tokens_amount ? parseInt(payload.tokens_amount) : null;
      payload.max_uses_total = payload.max_uses_total ? parseInt(payload.max_uses_total) : null;
      payload.max_uses_per_customer = parseInt(payload.max_uses_per_customer) || 1;
      payload.min_age = parseInt(payload.min_age) || 0;
      payload.is_speed_challenge = !!payload.is_speed_challenge;
      payload.speed_challenge_winners = payload.is_speed_challenge ? Math.max(1, parseInt(payload.speed_challenge_winners) || 1) : 1;
      payload.valid_from = payload.valid_from || null;
      payload.valid_until = payload.valid_until || null;

      // Build redemption_conditions (only include non-empty values)
      const conditions = {};
      if (rc.min_wait_days) conditions.min_wait_days = parseInt(rc.min_wait_days);
      if (rc.allowed_weekdays?.length > 0) conditions.allowed_weekdays = rc.allowed_weekdays;
      if (rc.time_from) conditions.time_from = rc.time_from;
      if (rc.time_until) conditions.time_until = rc.time_until;
      if (rc.min_visits) conditions.min_visits = parseInt(rc.min_visits);
      if (rc.min_spend) conditions.min_spend = parseFloat(rc.min_spend);
      if (rc.cooldown_period) {
        conditions.cooldown_period = rc.cooldown_period;
        conditions.cooldown_uses = parseInt(rc.cooldown_uses) || 1;
      }
      payload.redemption_conditions = Object.keys(conditions).length > 0 ? conditions : {};
      payload.scope = scope;

      if (isEdit) {
        await adminApi.put(`/admin/coupons/${coupon.id}`, payload);
        toast.success('Coupon aggiornato');
      } else {
        await adminApi.post('/admin/coupons', payload);
        toast.success('Coupon creato');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  const selectedType = COUPON_TYPES.find(t => t.id === form.coupon_type);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-[#1A1A1A]" data-testid="coupon-form-title">{isEdit ? 'Modifica Coupon' : 'Nuovo Coupon'}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-[#64748B]"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Coupon Type */}
          <div>
            <label className="text-xs font-semibold text-[#64748B] uppercase mb-2 block">Tipo Coupon</label>
            <div className="grid grid-cols-2 gap-2">
              {COUPON_TYPES.map(t => {
                const Icon = t.icon;
                return (
                  <button key={t.id} type="button" data-testid={`coupon-type-select-${t.id}`}
                    onClick={() => setForm(f => ({ ...f, coupon_type: t.id }))}
                    className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all text-left ${form.coupon_type === t.id ? 'border-[#D44E20] bg-[#D44E20]/5' : 'border-slate-100 hover:border-slate-200'}`}>
                    <Icon size={18} style={{ color: t.color }} />
                    <div>
                      <p className="text-sm font-semibold text-[#1A1A1A]">{t.label}</p>
                      <p className="text-[10px] text-[#64748B]">{t.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Code */}
          <div>
            <label className="text-xs font-semibold text-[#64748B] uppercase mb-1 block">Codice</label>
            <input data-testid="coupon-code-input" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="Es: WELCOME50 (lascia vuoto per auto-generare)"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]"
              disabled={isEdit} />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-semibold text-[#64748B] uppercase mb-1 block">Descrizione</label>
            <input data-testid="coupon-desc-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Descrizione visibile al cliente"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
          </div>

          {/* Type-specific fields */}
          {form.coupon_type === 'bonus_points' && (
            <div>
              <label className="text-xs font-semibold text-[#64748B] uppercase mb-1 block">Punti Bonus</label>
              <input data-testid="coupon-points-input" type="number" min="1" value={form.bonus_points}
                onChange={e => setForm(f => ({ ...f, bonus_points: e.target.value }))}
                placeholder="Es: 50" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
            </div>
          )}
          {form.coupon_type === 'discount' && (
            <div>
              <label className="text-xs font-semibold text-[#64748B] uppercase mb-1 block">Sconto %</label>
              <input data-testid="coupon-discount-input" type="number" min="1" max="100" value={form.discount_percent}
                onChange={e => setForm(f => ({ ...f, discount_percent: e.target.value }))}
                placeholder="Es: 20" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
            </div>
          )}
          {form.coupon_type === 'prize' && (
            <div>
              <label className="text-xs font-semibold text-[#64748B] uppercase mb-1 block">Nome Premio</label>
              <input data-testid="coupon-prize-input" value={form.prize_name}
                onChange={e => setForm(f => ({ ...f, prize_name: e.target.value }))}
                placeholder="Es: Taco Supreme Gratis" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
            </div>
          )}
          {form.coupon_type === 'game_token' && (
            <div>
              <label className="text-xs font-semibold text-[#64748B] uppercase mb-1 block">Numero Gettoni</label>
              <input data-testid="coupon-tokens-input" type="number" min="1" value={form.tokens_amount}
                onChange={e => setForm(f => ({ ...f, tokens_amount: e.target.value }))}
                placeholder="Es: 3" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
            </div>
          )}

          {form.coupon_type === 'voucher' && (
            <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-3" data-testid="voucher-config-section">
              <p className="text-xs font-semibold text-[#1A1A1A] mb-2">Buono monetario</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-[#64748B] block mb-1">Importo (€)</label>
                  <input data-testid="coupon-voucher-amount" type="number" min="1" step="0.5" value={form.voucher_amount}
                    onChange={e => setForm(f => ({ ...f, voucher_amount: e.target.value }))}
                    placeholder="Es: 50" className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
                </div>
                <div>
                  <label className="text-[10px] text-[#64748B] block mb-1">Min. per utilizzo (€)</label>
                  <input data-testid="coupon-voucher-min" type="number" min="0" step="0.5" value={form.voucher_min_per_use}
                    onChange={e => setForm(f => ({ ...f, voucher_min_per_use: e.target.value }))}
                    placeholder="Opzionale" className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
                </div>
              </div>
              <p className="text-[11px] text-sky-700 mt-2">
                💳 Il cliente potrà usare il buono in più volte fino a esaurimento. Es: buono da {form.voucher_amount || '50'}€ → usa 12€ oggi, 8€ domani, ecc. fino a 0€.
              </p>
            </div>
          )}

          {/* Rules */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#64748B] uppercase mb-1 block">Utilizzi Totali</label>
              <input data-testid="coupon-max-total-input" type="number" min="1" value={form.max_uses_total}
                onChange={e => setForm(f => ({ ...f, max_uses_total: e.target.value }))}
                placeholder="Illimitati" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#64748B] uppercase mb-1 block">Max per Cliente</label>
              <input data-testid="coupon-max-customer-input" type="number" min="1" value={form.max_uses_per_customer}
                onChange={e => setForm(f => ({ ...f, max_uses_per_customer: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
            </div>
          </div>

          {/* Speed challenge */}
          <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3" data-testid="speed-challenge-section">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1">
                <label className="text-xs font-semibold text-[#1A1A1A] flex items-center gap-1.5">
                  <span>⚡ Sfida velocità</span>
                  <span className="text-[10px] font-normal text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">Primo che riscatta vince</span>
                </label>
                <p className="text-[10px] text-[#64748B] mt-0.5">
                  Diffondi il codice/QR sui social: solo i primi N clienti che lo aggiungono alla bacheca lo ottengono. Poi diventa non disponibile per tutti gli altri.
                </p>
              </div>
              <button type="button" data-testid="coupon-speed-toggle"
                onClick={() => setForm(f => ({ ...f, is_speed_challenge: !f.is_speed_challenge }))}
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${form.is_speed_challenge ? 'bg-amber-500' : 'bg-slate-300'}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.is_speed_challenge ? 'translate-x-5' : ''}`} />
              </button>
            </div>
            {form.is_speed_challenge && (
              <div className="mt-3 pt-3 border-t border-amber-200">
                <label className="text-xs font-semibold text-[#64748B] uppercase mb-1 block">Numero di vincitori</label>
                <input data-testid="coupon-speed-winners-input" type="number" min="1" value={form.speed_challenge_winners}
                  onChange={e => setForm(f => ({ ...f, speed_challenge_winners: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl border border-amber-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400" />
                <p className="text-[10px] text-[#64748B] mt-1">Es. 1 = solo il primo vince · 3 = i primi 3 in classifica vincono.</p>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-[#64748B] uppercase mb-1 block">Tier Minimo</label>
            <div className="flex gap-2">
              {TIERS.map(tier => (
                <button key={tier} type="button" data-testid={`coupon-tier-${tier}`}
                  onClick={() => setForm(f => ({ ...f, min_tier: tier }))}
                  className={`flex-1 py-2 rounded-xl text-xs font-semibold capitalize transition-all ${form.min_tier === tier ? 'bg-[#D44E20] text-white' : 'bg-gray-50 text-[#64748B] hover:bg-slate-100'}`}>
                  {tier}
                </button>
              ))}
            </div>
          </div>

          {/* Parental control: age restriction */}
          <div className="rounded-xl border border-rose-100 bg-rose-50/40 p-3" data-testid="age-restriction-section">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1">
                <label className="text-xs font-semibold text-[#1A1A1A] flex items-center gap-1.5">
                  <span>🔞 Restrizione età</span>
                  <span className="text-[10px] font-normal text-rose-600 bg-rose-100 rounded-full px-2 py-0.5">Es: alcolici</span>
                </label>
                <p className="text-[10px] text-[#64748B] mt-0.5">
                  Imposta età minima richiesta per riscattare. Bloccato anche nelle estrazioni delle campagne fisiche.
                </p>
              </div>
              <select
                data-testid="coupon-min-age"
                value={form.min_age}
                onChange={e => setForm(f => ({ ...f, min_age: parseInt(e.target.value) || 0 }))}
                className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]"
              >
                <option value="0">Nessuna</option>
                <option value="14">14+</option>
                <option value="16">16+</option>
                <option value="18">18+ (alcolici)</option>
                <option value="21">21+</option>
              </select>
            </div>
            {form.min_age >= 18 && (
              <p className="text-[11px] text-rose-700 mt-2">
                ⚠️ I minorenni non potranno né riscattare questo coupon né riceverlo come premio in una campagna fisica. Se la data di nascita non è registrata, il riscatto verrà bloccato per cautela.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#64748B] uppercase mb-1 block">Valido dal</label>
              <input data-testid="coupon-valid-from-input" type="date" value={form.valid_from}
                onChange={e => setForm(f => ({ ...f, valid_from: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#64748B] uppercase mb-1 block">Valido fino al</label>
              <input data-testid="coupon-valid-until-input" type="date" value={form.valid_until}
                onChange={e => setForm(f => ({ ...f, valid_until: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
            </div>
          </div>

          <div className="flex items-center justify-between py-2">
            <span className="text-sm font-medium text-[#1A1A1A]">Attivo</span>
            <button type="button" onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))} data-testid="coupon-active-toggle">
              {form.is_active ? <ToggleRight size={28} className="text-[#3ECF8E]" /> : <ToggleLeft size={28} className="text-gray-300" />}
            </button>
          </div>

          {/* Scope (multi-venue) */}
          <ScopeSelector value={scope} onChange={setScope} />

          {/* Redemption Conditions */}
          <div className="border-t border-slate-100 pt-4">
            <p className="text-xs font-semibold text-[#64748B] uppercase mb-3">Condizioni di riscossione (facoltative)</p>

            <div className="space-y-3">
              {/* Min wait days */}
              <div>
                <label className="text-xs font-medium text-[#1A1A1A] mb-1 block">Giorni di attesa</label>
                <p className="text-[10px] text-[#64748B] mb-1">Il cliente deve aspettare X giorni prima di poter riscuotere</p>
                <input data-testid="rc-min-wait" type="number" min="0" value={rc.min_wait_days}
                  onChange={e => setRc(r => ({ ...r, min_wait_days: e.target.value }))}
                  placeholder="0 = nessun vincolo"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
              </div>

              {/* Allowed weekdays */}
              <div>
                <label className="text-xs font-medium text-[#1A1A1A] mb-1 block">Giorni consentiti</label>
                <p className="text-[10px] text-[#64748B] mb-2">Seleziona i giorni in cui il coupon è riscuotibile. Nessuno selezionato = tutti i giorni.</p>
                <div className="flex gap-1.5" data-testid="rc-weekdays">
                  {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((d, i) => (
                    <button key={i} type="button" data-testid={`rc-day-${i}`}
                      onClick={() => setRc(r => ({
                        ...r,
                        allowed_weekdays: r.allowed_weekdays.includes(i)
                          ? r.allowed_weekdays.filter(x => x !== i)
                          : [...r.allowed_weekdays, i],
                      }))}
                      className={`flex-1 py-2 rounded-lg text-[11px] font-semibold transition-all ${
                        rc.allowed_weekdays.includes(i)
                          ? 'bg-[#D44E20] text-white'
                          : 'bg-slate-50 text-[#64748B] hover:bg-slate-100'
                      }`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              {/* Time window */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#1A1A1A] mb-1 block">Orario dalle</label>
                  <input data-testid="rc-time-from" type="time" value={rc.time_from}
                    onChange={e => setRc(r => ({ ...r, time_from: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#1A1A1A] mb-1 block">Orario alle</label>
                  <input data-testid="rc-time-until" type="time" value={rc.time_until}
                    onChange={e => setRc(r => ({ ...r, time_until: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
                </div>
              </div>

              {/* Min visits */}
              <div>
                <label className="text-xs font-medium text-[#1A1A1A] mb-1 block">Visite minime</label>
                <p className="text-[10px] text-[#64748B] mb-1">Il cliente deve avere almeno X visite totali</p>
                <input data-testid="rc-min-visits" type="number" min="0" value={rc.min_visits}
                  onChange={e => setRc(r => ({ ...r, min_visits: e.target.value }))}
                  placeholder="0 = nessun vincolo"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
              </div>

              {/* Min spend */}
              <div>
                <label className="text-xs font-medium text-[#1A1A1A] mb-1 block">Spesa minima nella visita</label>
                <p className="text-[10px] text-[#64748B] mb-1">Il cliente deve spendere almeno X euro nella stessa visita</p>
                <div className="flex items-center gap-2">
                  <input data-testid="rc-min-spend" type="number" min="0" step="0.5" value={rc.min_spend}
                    onChange={e => setRc(r => ({ ...r, min_spend: e.target.value }))}
                    placeholder="0 = nessun vincolo"
                    className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
                  <span className="text-sm text-[#64748B] font-medium">€</span>
                </div>
              </div>

              {/* Cooldown / Riscatto multiplo */}
              <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-3" data-testid="rc-cooldown-section">
                <label className="text-xs font-semibold text-[#1A1A1A] mb-1 block flex items-center gap-1.5">
                  Riscatto ricorrente
                  <span className="text-[10px] font-normal text-blue-600 bg-blue-100 rounded-full px-2 py-0.5">Es: tacos gratis per un mese</span>
                </label>
                <p className="text-[10px] text-[#64748B] mb-2">
                  Limita quanti riscatti lo stesso cliente può fare nello stesso giorno/settimana/mese.
                  Combinalo con "Validità fino al" per coupon stagionali ricorrenti.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    data-testid="rc-cooldown-period"
                    value={rc.cooldown_period}
                    onChange={e => setRc(r => ({ ...r, cooldown_period: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]"
                  >
                    <option value="">Nessun limite ricorrente</option>
                    <option value="day">Max al giorno</option>
                    <option value="week">Max a settimana</option>
                    <option value="month">Max al mese</option>
                  </select>
                  <input
                    data-testid="rc-cooldown-uses"
                    type="number"
                    min="1"
                    value={rc.cooldown_uses}
                    disabled={!rc.cooldown_period}
                    onChange={e => setRc(r => ({ ...r, cooldown_uses: e.target.value }))}
                    placeholder="1"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] disabled:bg-slate-50 disabled:text-slate-400"
                  />
                </div>
                {rc.cooldown_period && (
                  <p className="text-[11px] text-blue-700 mt-2">
                    💡 Il cliente potrà riscuotere massimo <strong>{rc.cooldown_uses || 1}</strong> volta{(rc.cooldown_uses > 1) ? 'e' : ''} {rc.cooldown_period === 'day' ? 'al giorno' : rc.cooldown_period === 'week' ? 'a settimana' : 'al mese'}. Se ha già riscosso, dovrà aspettare {rc.cooldown_period === 'day' ? 'il giorno successivo' : rc.cooldown_period === 'week' ? 'la prossima settimana' : 'il prossimo mese'}.
                  </p>
                )}
              </div>
            </div>
          </div>

          <button type="submit" disabled={saving} data-testid="coupon-save-btn"
            className="w-full py-3 bg-[#D44E20] text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50">
            {saving ? 'Salvataggio...' : isEdit ? 'Salva Modifiche' : 'Crea Coupon'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [cRes, sRes] = await Promise.all([
        adminApi.get('/admin/coupons', { params: { limit: 100, ...(typeFilter ? { coupon_type: typeFilter } : {}) } }),
        adminApi.get('/admin/coupons-stats'),
      ]);
      setCoupons(cRes.data.coupons);
      setStats(sRes.data);
    } catch { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  }, [typeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo coupon?')) return;
    try {
      await adminApi.delete(`/admin/coupons/${id}`);
      toast.success('Coupon eliminato');
      fetchData();
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  const handleToggle = async (coupon) => {
    try {
      await adminApi.put(`/admin/coupons/${coupon.id}`, { is_active: !coupon.is_active });
      toast.success(coupon.is_active ? 'Coupon disattivato' : 'Coupon attivato');
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Codice ${code} copiato!`);
  };

  const filtered = coupons.filter(c =>
    !search || c.code.includes(search.toUpperCase()) || (c.description || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="admin-coupons-page">
      <PageTooltip page="coupons" />
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">Coupon & Gettoni</h1>
          <p className="text-[#64748B] text-sm">Gestisci coupon, sconti, premi e gettoni gioco</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-coupon-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-[#D44E20] text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity">
          <Plus size={16} /> Nuovo Coupon
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="stat-total-coupons">
            <p className="text-[10px] font-semibold text-[#64748B] uppercase">Totali</p>
            <p className="text-2xl font-bold text-[#1A1A1A]">{stats.total_coupons}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="stat-active-coupons">
            <p className="text-[10px] font-semibold text-[#64748B] uppercase">Attivi</p>
            <p className="text-2xl font-bold text-[#3ECF8E]">{stats.active_coupons}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="stat-total-redemptions">
            <p className="text-[10px] font-semibold text-[#64748B] uppercase">Utilizzi</p>
            <p className="text-2xl font-bold text-[#2196F3]">{stats.total_redemptions}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="stat-by-type">
            <p className="text-[10px] font-semibold text-[#64748B] uppercase">Per Tipo</p>
            <div className="flex gap-1 mt-1 flex-wrap">
              {COUPON_TYPES.map(t => (
                <span key={t.id} className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: t.color + '18', color: t.color }}>
                  {stats.by_type?.[t.id] || 0}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <input data-testid="coupon-search-input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Cerca per codice o descrizione..."
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]" />
        </div>
        <div className="relative">
          <select data-testid="coupon-type-filter" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20]">
            <option value="">Tutti i tipi</option>
            {COUPON_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] pointer-events-none" />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Ticket size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-[#64748B] text-sm mb-4">{search || typeFilter ? 'Nessun coupon trovato' : 'Nessun coupon creato'}</p>
          {!search && !typeFilter && (
            <button onClick={() => setShowForm(true)} className="text-[#D44E20] text-sm font-medium">Crea il primo coupon</button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(c => (
            <div key={c.id} data-testid={`coupon-card-${c.code}`}
              className={`bg-white rounded-xl p-4 border transition-all ${c.is_active ? 'border-slate-100 hover:border-slate-200' : 'border-slate-100 opacity-60'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <button onClick={() => copyCode(c.code)} data-testid={`coupon-copy-${c.code}`}
                      className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors" title="Copia codice">
                      <span className="font-mono text-sm font-bold text-[#1A1A1A]">{c.code}</span>
                      <Copy size={12} className="text-[#64748B]" />
                    </button>
                    <TypeBadge type={c.coupon_type} />
                    {c.min_age >= 18 && (
                      <span className="text-[10px] font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-lg" title="Solo per maggiorenni">
                        🔞 {c.min_age}+
                      </span>
                    )}
                    {!c.is_active && <span className="text-[10px] font-semibold text-red-500 bg-red-50 px-2 py-0.5 rounded-lg">Disattivato</span>}
                    {c.is_speed_challenge && (
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-lg" data-testid={`coupon-speed-badge-${c.code}`} title={`Sfida velocità · ${c.speed_challenge_winners || 1} vincitor${(c.speed_challenge_winners || 1) > 1 ? 'i' : 'e'}`}>
                        ⚡ Sfida velocità
                        {(() => { const won = (c.speed_winner_ids || c.speed_winners || []).length; const lim = c.speed_challenge_winners || 1; return ` · ${won}/${lim}`; })()}
                      </span>
                    )}
                  </div>
                  {c.is_speed_challenge && (c.speed_winners || []).length > 0 && (
                    <p className="text-[11px] text-amber-700 mb-1" data-testid={`coupon-speed-winners-${c.code}`}>
                      🏆 Vinto da: {(c.speed_winners || []).map(w => w.name || 'Cliente').join(', ')}
                    </p>
                  )}
                  {c.description && <p className="text-sm text-[#64748B] mb-1">{c.description}</p>}
                  <div className="flex items-center gap-3 text-[11px] text-[#64748B]">
                    <span>Utilizzi: <strong className="text-[#1A1A1A]">{c.uses_count || 0}</strong>{c.max_uses_total ? `/${c.max_uses_total}` : ''}</span>
                    <span>Tier min: <strong className="text-[#1A1A1A] capitalize">{c.min_tier}</strong></span>
                    {c.valid_until && <span>Scade: <strong className="text-[#1A1A1A]">{new Date(c.valid_until).toLocaleDateString('it-IT')}</strong></span>}
                    {c.coupon_type === 'bonus_points' && <span>Valore: <strong className="text-[#3ECF8E]">{c.bonus_points}pt</strong></span>}
                    {c.coupon_type === 'discount' && <span>Valore: <strong className="text-[#2196F3]">{c.discount_percent}%</strong></span>}
                    {c.coupon_type === 'voucher' && <span>Buono: <strong className="text-[#0EA5E9]">{Number(c.voucher_amount || 0).toFixed(2)}€</strong></span>}
                    {c.coupon_type === 'prize' && <span>Premio: <strong className="text-[#9C27B0]">{c.prize_name}</strong></span>}
                    {c.coupon_type === 'game_token' && <span>Gettoni: <strong className="text-[#FF9800]">{c.tokens_amount}</strong></span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => handleToggle(c)} data-testid={`coupon-toggle-${c.code}`}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-50 transition-colors" title={c.is_active ? 'Disattiva' : 'Attiva'}>
                    {c.is_active ? <ToggleRight size={18} className="text-[#3ECF8E]" /> : <ToggleLeft size={18} className="text-gray-300" />}
                  </button>
                  <button onClick={() => { setEditing(c); setShowForm(true); }} data-testid={`coupon-edit-${c.code}`}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-50 transition-colors"><Pencil size={14} className="text-[#64748B]" /></button>
                  <button onClick={() => handleDelete(c.id)} data-testid={`coupon-delete-${c.code}`}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors"><Trash2 size={14} className="text-[#64748B] hover:text-red-500" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <CouponForm
          coupon={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSave={() => { setShowForm(false); setEditing(null); fetchData(); }}
        />
      )}
    </div>
  );
}
